import React, { createContext, useContext, useState, useEffect } from "react"

const CartContext = createContext()

const CART_STORAGE_KEY = "kitchenwareuk_cart"

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([])
  const [isLoaded, setIsLoaded] = useState(false)

  // Load cart from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY)
      if (savedCart) {
        try {
          setCart(JSON.parse(savedCart))
        } catch (e) {
          console.error("Failed to parse cart from localStorage:", e)
        }
      }
      setIsLoaded(true)
    }
  }, [])

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (isLoaded && typeof window !== "undefined") {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart))
    }
  }, [cart, isLoaded])

  const addToCart = (product) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.asin === product.asin)
      if (existingItem) {
        return prevCart.map((item) =>
          item.asin === product.asin
            ? { ...item, quantity: item.quantity + (product.quantity || 1) }
            : item
        )
      }
      return [...prevCart, { ...product, quantity: product.quantity || 1 }]
    })
  }

  const removeFromCart = (asin) => {
    setCart((prevCart) => prevCart.filter((item) => item.asin !== asin))
  }

  const updateQuantity = (asin, quantity) => {
    if (quantity <= 0) {
      removeFromCart(asin)
      return
    }
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.asin === asin ? { ...item, quantity } : item
      )
    )
  }

  const clearCart = () => {
    setCart([])
  }

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      // Use priceSale if available, otherwise use priceRRP, otherwise use legacy price field
      const price = parseFloat(item.priceSale || item.priceRRP || item.price) || 0
      return total + price * item.quantity
    }, 0)
  }

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0)
  }

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    isLoaded,
  }

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}

